import { Button } from '@design-system';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useContext, useEffect } from 'react';

import { AuthEntryPoint } from '../features/auth/types';
import { useIsSubscribed } from '../hooks/subscription';
import { useAuthenticate } from '../hooks/useAuthenticate';
import { useAuth } from '../providers';
import { AccountContext } from '../providers/AccountProvider';
import { ReleaseSubscriptionCtaStoryblokExtended } from '../types/Franchises';
import { GeneratedSubscriptionType } from '../types/generated';
import { Image } from './Image';
import { showPhoneStep } from '../helpers/phone';

export const ContentBlockSubscriptionReleases = ({
  blok,
}: {
  blok: ReleaseSubscriptionCtaStoryblokExtended;
}) => {
  const { t } = useTranslation(['releases', 'authentication']);
  const { user } = useAuth();
  const { onboarding } = useContext(AccountContext);

  const { authenticateAndSubscribe } = useAuthenticate();
  const releaseSubscriptions = [
    {
      objectId: blok.subscriptionId,
      type: GeneratedSubscriptionType.Release,
      name: blok.subscriptionName || '',
      ref: '',
    },
  ];

  if (blok.HubspotListId) {
    releaseSubscriptions.push({
      objectId: blok.HubspotListId,
      type: GeneratedSubscriptionType.HubspotList,
      name: blok.HubspotListId || '',
      ref: '',
    });
  }

  const artistSubscriptions = blok.subscriptionArtists.map((artist) => {
    return {
      objectId: artist.id,
      type: GeneratedSubscriptionType.Artist,
      name: artist.name || '',
      ref: '',
    };
  });

  const blokSubscriptions = [...releaseSubscriptions, ...artistSubscriptions];

  const {
    isSubscribedAll: isSubscribed,
    loading,
    refetch,
  } = useIsSubscribed(blokSubscriptions);

  useEffect(() => {
    if (!user && isSubscribed) refetch();
  }, [isSubscribed, refetch, user]);

  const handleSubscribe = async () => {
    await authenticateAndSubscribe({
      authScreenThumbnail: blok.image?.filename || '',
      authScreenTitle: t('authentication:dynamicTitle.product'),
      subscriptions: blokSubscriptions,
      analyticsProperties: {
        'artist name': blok.subscriptionArtists
          .map(({ name }) => name)
          .join(','),
        artist_id: blok.subscriptionArtists.map(({ id }) => id).join(','),
        button_location: 'release page',
        release_id: blok.subscriptionId,
        source: AuthEntryPoint.Release,
        subscription_name: blok.subscriptionName,
      },
      showOnboarding: !showPhoneStep(user),
      showPhoneStep: showPhoneStep(user),
      hideSuccessScreen: !onboarding?.isOnboardingStarted,
      onFlowComplete: () => {
        onboarding?.show({
          page: AuthEntryPoint.Release,
          id: blok.subscriptionId,
        });
      },
    });
    refetch();
  };

  return (
    <div>
      <div
        className={classNames(
          'mx-auto flex w-full overflow-hidden rounded-sm border border-divider/20 bg-white sm:rounded-md md:max-w-2xl',
          {
            'p4 md:p-6': blok.image?.id,
          },
        )}
      >
        <div className="relative flex w-full flex-col sm:flex-row">
          {blok.image?.filename ? (
            <div className="relative w-full flex-1 overflow-hidden rounded-sm">
              <Image
                alt={blok.image.alt || ''}
                className="z-10"
                eagerLoading
                focus={blok.image.focus}
                size={{
                  sm: { ratio: '3/4', span: 12 },
                  md: { ratio: '3/4', span: 4 },
                  lg: { ratio: '3/4', span: 4 },
                }}
                src={blok.image.filename}
              />
            </div>
          ) : null}

          <div className="relative z-30 flex w-full flex-col items-center justify-center space-y-4 p-6 sm:flex-1">
            <div className="flex w-full max-w-sm flex-col items-center text-center">
              <p className="text-pretty font-bold">
                {blok.title || t('releases:subscriptionRelease.title')}
              </p>
              <p className="text-pretty">
                {blok.subtitle || t('releases:subscriptionRelease.subtitle')}
              </p>
            </div>

            <Button
              disabled={isSubscribed}
              icon="ic_notification_outline"
              label={
                isSubscribed
                  ? t('releases:subscriptionRelease.ctaLabel.positive')
                  : blok.ctaLabel ||
                    t('releases:subscriptionRelease.ctaLabel.default')
              }
              loading={loading}
              onClick={() => {
                handleSubscribe();
              }}
              size="lg"
              type="button"
              variant={isSubscribed ? 'positive' : 'primary'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
